import { ReactNode } from "react";
import { CreditCard, HelpCircle, Bell, Mail, Layers, Menu, Home, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LocalizedLink as Link } from "@/components/LocalizedLink";
import { JsonLogo } from "@/components/JsonLogo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { UserMenu } from "@/components/UserMenu";
import { CreditsDisplay } from "@/components/CreditsDisplay";
import { NotificationBell } from "@/components/NotificationBell";
import { HeaderNavLink } from "@/components/HeaderNavLink";
import { AppBreadcrumb } from "@/components/AppBreadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";

interface MainLayoutProps {
  children: ReactNode;
  showBackgroundEffects?: boolean;
}

export const MainLayout = ({ children, showBackgroundEffects = true }: MainLayoutProps) => {
  const { language } = useLanguage();
  const { isAuthenticated } = useAuth();
  const { currentPlan } = useSubscription();
  const hasMonitoring = currentPlan?.id === 'pro' || currentPlan?.id === 'unlimited';

  return (
    <div className="min-h-screen bg-background">
      {/* Background effects */}
      {showBackgroundEffects && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ contain: 'strict' }}>
          {/* Gradient orbs */}
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" style={{ willChange: 'transform' }} />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary/3 rounded-full blur-3xl" style={{ willChange: 'transform' }} />
          
          {/* Code background effect */}
          <div className="absolute inset-0 opacity-[0.03] select-none" aria-hidden="true" style={{ contain: 'layout style paint', contentVisibility: 'auto' as any }}>
            <pre className="font-mono text-[10px] leading-relaxed text-foreground whitespace-pre-wrap break-all p-8">
{`{
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": "SchemasGen",
  "url": "https://schemasgen.com",
  "applicationCategory": "SEO Tool",
  "offers": {
    "@type": "AggregateOffer",
    "lowPrice": "0",
    "highPrice": "89",
    "priceCurrency": "EUR"
  }
}
{
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "SchemasGen",
  "url": "https://schemasgen.com",
  "logo": "https://schemasgen.com/favicon.svg",
  "email": "contact@schemasgen.com"
}
{
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [{
    "@type": "Question",
    "name": "What is JSON-LD?",
    "acceptedAnswer": {
      "@type": "Answer",
      "text": "A format to describe a page with structured data."
    }
  }]
}
{
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [{
    "@type": "ListItem",
    "position": 1,
    "name": "SchemasGen",
    "item": "https://schemasgen.com"
  }]
}`}
            </pre>
          </div>
        </div>
      )}

      {/* Navigation bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-sm border-b border-border/50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <div className="p-2 bg-primary/10 rounded-lg border border-primary/20">
              <JsonLogo size={20} />
            </div>
            <span className="hidden sm:inline font-bold text-lg">
              <span className="text-primary">Schemas</span>
              <span className="text-foreground">Gen</span>
            </span>
          </Link>

          {/* Center menu (desktop) */}
          <div className="hidden md:flex items-center gap-1 bg-muted/50 backdrop-blur-sm rounded-full px-2 py-1 border border-border/50">
            <HeaderNavLink to="/structured-data" icon={Layers}>
              {language === 'fr' ? 'Données structurées' : language === 'es' ? 'Datos estructurados' : 'Structured data'}
            </HeaderNavLink>
            <HeaderNavLink to="/pricing" icon={CreditCard}>
              {language === 'fr' ? 'Tarifs' : language === 'es' ? 'Precios' : 'Pricing'}
            </HeaderNavLink>
            {isAuthenticated && hasMonitoring && (
              <HeaderNavLink to="/account?tab=monitoring" icon={Bell}>
                Monitoring
              </HeaderNavLink>
            )}
            <HeaderNavLink to="/#faq" icon={HelpCircle}>
              FAQ
            </HeaderNavLink>
            <HeaderNavLink to="/contact" icon={Mail}>
              {language === 'fr' ? 'Contact' : language === 'es' ? 'Contacto' : 'Contact'}
            </HeaderNavLink>
          </div>

          {/* Mobile menu */}
          <div className="md:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-9 w-9" aria-label="Menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to="/"><Home className="mr-2 h-4 w-4" />{language === 'fr' ? 'Accueil' : language === 'es' ? 'Inicio' : 'Home'}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to="/structured-data"><Layers className="mr-2 h-4 w-4" />{language === 'fr' ? 'Données structurées' : language === 'es' ? 'Datos estructurados' : 'Structured data'}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to="/pricing"><CreditCard className="mr-2 h-4 w-4" />{language === 'fr' ? 'Tarifs' : language === 'es' ? 'Precios' : 'Pricing'}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to="/docs"><FileText className="mr-2 h-4 w-4" />{language === 'fr' ? 'Documentation API' : language === 'es' ? 'Documentación API' : 'API Docs'}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to="/contact"><Mail className="mr-2 h-4 w-4" />{language === 'fr' ? 'Contact' : language === 'es' ? 'Contacto' : 'Contact'}</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
            {isAuthenticated && (
              <>
                <CreditsDisplay />
                <NotificationBell />
              </>
            )}
            <LanguageSwitcher />
            <UserMenu />
          </div>
        </div>
      </nav>

      {/* Main content with padding for fixed nav */}
      <main className="pt-16">
        <div className="max-w-6xl mx-auto px-4 mt-4">
          <AppBreadcrumb />
        </div>
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-border/50 mt-20">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Brand */}
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-primary/10 rounded-md border border-primary/20">
                <JsonLogo size={14} />
              </div>
              <span className="text-sm text-muted-foreground">
                © {new Date().getFullYear()} <span className="text-primary font-medium">Schemas</span>Gen
              </span>
            </div>

            {/* Links */}
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
              <Link to="/structured-data" className="hover:text-foreground transition-colors">
                {language === 'fr' ? 'Guide des données structurées' : language === 'es' ? 'Guía de datos estructurados' : 'Structured data guide'}
              </Link>
              <Link to="/pricing" className="hover:text-foreground transition-colors">
                {language === 'fr' ? 'Tarifs' : language === 'es' ? 'Precios' : 'Pricing'}
              </Link>
              <Link to="/docs" className="hover:text-foreground transition-colors">
                {language === 'fr' ? 'Documentation API' : language === 'es' ? 'Documentación API' : 'API Documentation'}
              </Link>
              <Link to="/contact" className="hover:text-foreground transition-colors">
                {language === 'fr' ? 'Contact' : language === 'es' ? 'Contacto' : 'Contact'}
              </Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">
                {language === 'fr' ? 'CGV' : language === 'es' ? 'Términos' : 'Terms'}
              </Link>
              <Link to="/privacy" className="hover:text-foreground transition-colors">
                {language === 'fr' ? 'Confidentialité' : language === 'es' ? 'Privacidad' : 'Privacy'}
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
